import {
  fetchAntivirusDevices,
  fetchM365Devices,
  fetchOnlineDevices,
  fetchOsDevices,
  fetchPostureAttention,
  fetchPostureDevices,
} from '@/api/charts'
import { fetchBackupHosts } from '@/api/backup'
import { fetchNasHosts } from '@/api/nas'
import { downloadCsv } from '@/lib/csvExport'
import { TRAFFIC } from '@/lib/trafficLight'

const EXPORT_PAGE_SIZE = 200

const BACKUP_STATUS_MAP = {
  OK: 'PASS',
  Review: 'WARN',
  'Action needed': 'FAIL',
  'No data': 'NONE',
}

const ANTIVIRUS_BUCKET_MAP = {
  'Virus detected': 'virus_detected',
  'No AV': 'no_av',
  'AV not updated': 'av_not_updated',
  OK: 'ok',
  'No data': 'no_data',
}

function stamp() {
  return new Date().toISOString().slice(0, 10)
}

function slugPart(value) {
  return String(value || 'export')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48)
}

function postureLabel(status) {
  return TRAFFIC[status]?.label || status || ''
}

async function fetchAllPages(fetchPage) {
  const first = await fetchPage(1)
  const items = [...(first.items || [])]
  const pages = first.pages || 1
  for (let page = 2; page <= pages; page += 1) {
    const next = await fetchPage(page)
    items.push(...(next.items || []))
  }
  return { items, meta: first }
}

/** One CSV column per reason (Yes / blank) instead of a semicolon-joined cell. */
function reasonColumnsFromItems(items, reasonCounts = []) {
  const byId = new Map()
  for (const row of reasonCounts || []) {
    if (row?.id) byId.set(row.id, row.label || row.id)
  }
  for (const device of items) {
    for (const reason of device.reasons || []) {
      if (!reason?.id) continue
      if (!byId.has(reason.id)) byId.set(reason.id, reason.label || reason.id)
    }
  }
  // Prefer API reason_counts order, then any extras found on devices.
  const orderedIds = []
  for (const row of reasonCounts || []) {
    if (row?.id && byId.has(row.id)) orderedIds.push(row.id)
  }
  for (const id of byId.keys()) {
    if (!orderedIds.includes(id)) orderedIds.push(id)
  }
  return orderedIds.map((id) => ({ id, label: byId.get(id) }))
}

function postureDeviceExportRows(items, { showCustomer = false, reasonCounts = [] } = {}) {
  const reasonCols = reasonColumnsFromItems(items, reasonCounts)
  const headers = [
    'Device',
    'Display name',
    ...(showCustomer ? ['Customer'] : []),
    ...reasonCols.map((col) => col.label),
    'Security',
    'Hardware',
    'Agent ID',
  ]
  const rows = items.map((device) => {
    const present = new Set((device.reasons || []).map((r) => r.id).filter(Boolean))
    return [
      device.hostname || `Device ${device.agent_id}`,
      device.display_name || '',
      ...(showCustomer ? [device.customer_name || ''] : []),
      ...reasonCols.map((col) => (present.has(col.id) ? 'Yes' : '')),
      postureLabel(device.security_status),
      postureLabel(device.hardware_status),
      device.agent_id ?? '',
    ]
  })
  return { headers, rows }
}

function backupBucket(host) {
  const status = String(host?.overall_status || '').toUpperCase()
  if (status === 'PASS') return 'PASS'
  if (status === 'WARN') return 'WARN'
  if (status === 'FAIL') return 'FAIL'
  return 'NONE'
}

/** Export Security/Hardware pie drill-down (respects reason filter). */
export async function exportPostureDrillDownCsv({
  customerIds,
  area,
  status,
  reason = null,
  reasonLabel = null,
  showCustomer = false,
}) {
  const { items, meta } = await fetchAllPages((page) =>
    fetchPostureDevices(customerIds, area, status, page, EXPORT_PAGE_SIZE, reason),
  )

  const { headers, rows } = postureDeviceExportRows(items, {
    showCustomer,
    reasonCounts: meta?.reason_counts,
  })

  const bits = ['posture', area, status]
  if (reason) bits.push(reasonLabel || reason)
  downloadCsv(`${bits.map(slugPart).join('-')}-${stamp()}.csv`, headers, rows)
  return items.length
}

/** Export Devices needing attention list (optional reason filter). */
export async function exportPostureAttentionCsv({
  customerIds,
  reason = null,
  reasonLabel = null,
  showCustomer = false,
}) {
  const { items, meta } = await fetchAllPages((page) =>
    fetchPostureAttention(customerIds, page, EXPORT_PAGE_SIZE, reason),
  )

  const { headers, rows } = postureDeviceExportRows(items, {
    showCustomer,
    reasonCounts: meta?.reason_counts,
  })

  const bits = ['attention']
  if (reason) bits.push(reasonLabel || reason)
  downloadCsv(`${bits.map(slugPart).join('-')}-${stamp()}.csv`, headers, rows)
  return items.length
}

/** Export Online / OS / Antivirus / Backup pie slice lists. */
export async function exportChartSliceDrillDownCsv({
  filter,
  customerIds,
  showCustomer = false,
}) {
  if (!filter?.chart || !filter?.key) return 0

  if (filter.chart === 'backup') {
    const bucket = BACKUP_STATUS_MAP[filter.key]
    const [server, nas] = await Promise.all([
      fetchBackupHosts(customerIds).catch(() => ({ hosts: [] })),
      fetchNasHosts(customerIds).catch(() => ({ hosts: [] })),
    ])
    const combined = [
      ...(server.hosts || []).map((host) => ({ ...host, source: 'server' })),
      ...(nas.hosts || []).map((host) => ({ ...host, source: 'nas' })),
    ]
      .filter((host) => backupBucket(host) === bucket)
      .sort((a, b) =>
        String(a.hostname || '').localeCompare(String(b.hostname || ''), 'en', {
          sensitivity: 'base',
        }),
      )

    const headers = [
      'Host',
      ...(showCustomer ? ['Customer'] : []),
      'Source',
      'Status',
      'Detail',
      'Agent ID',
    ]
    const rows = combined.map((host) => [
      host.hostname || '',
      ...(showCustomer ? [host.customer_name || ''] : []),
      host.source === 'nas' ? 'NAS' : 'Server',
      host.overall_status || 'No data',
      host.interpretation || '',
      host.agent_id ?? '',
    ])
    downloadCsv(`backup-${slugPart(filter.key)}-${stamp()}.csv`, headers, rows)
    return combined.length
  }

  let items = []
  if (filter.chart === 'online') {
    const online = filter.key === 'Online'
    ;({ items } = await fetchAllPages((page) =>
      fetchOnlineDevices(customerIds, online, page, EXPORT_PAGE_SIZE),
    ))
  } else if (filter.chart === 'os') {
    ;({ items } = await fetchAllPages((page) =>
      fetchOsDevices(customerIds, filter.key, page, EXPORT_PAGE_SIZE),
    ))
  } else if (filter.chart === 'antivirus') {
    const bucket =
      filter.bucket || ANTIVIRUS_BUCKET_MAP[filter.key] || filter.key
    ;({ items } = await fetchAllPages((page) =>
      fetchAntivirusDevices(customerIds, bucket, page, EXPORT_PAGE_SIZE),
    ))
  } else {
    return 0
  }

  const detailHeader =
    filter.chart === 'online'
      ? 'Status'
      : filter.chart === 'os'
        ? 'Operating system'
        : 'Issue'

  const headers = [
    'Device',
    'Display name',
    ...(showCustomer ? ['Customer'] : []),
    detailHeader,
    'Agent ID',
  ]
  const rows = items.map((item) => {
    let detail = ''
    if (filter.chart === 'online') {
      detail = item.online_status ? 'Online' : 'Offline'
    } else if (filter.chart === 'os') {
      detail = item.os_name || 'Unknown'
    } else {
      detail =
        (item.issues && item.issues[0]) ||
        item.label ||
        filter.key
      if (item.cover_label) detail = `${detail}; Cover: ${item.cover_label}`
      else if (item.av_product) detail = `${detail}; AV: ${item.av_product}`
    }
    return [
      item.hostname || (item.agent_id ? `Device ${item.agent_id}` : ''),
      item.display_name || '',
      ...(showCustomer ? [item.customer_name || ''] : []),
      detail,
      item.agent_id ?? '',
    ]
  })

  downloadCsv(
    `${slugPart(filter.chart)}-${slugPart(filter.key)}-${stamp()}.csv`,
    headers,
    rows,
  )
  return items.length
}

/** Export Microsoft 365 pie drill-down. */
export async function exportM365DrillDownCsv({
  customerIds,
  area,
  status,
  showCustomer = false,
}) {
  const { items } = await fetchAllPages((page) =>
    fetchM365Devices(customerIds, area, status, page, EXPORT_PAGE_SIZE),
  )

  const headers = [
    'Device',
    'Display name',
    ...(showCustomer ? ['Customer'] : []),
    'Status',
    'Detail',
    'Agent ID',
  ]
  const rows = items.map((device) => [
    device.hostname || `Device ${device.agent_id}`,
    device.display_name || '',
    ...(showCustomer ? [device.customer_name || ''] : []),
    postureLabel(device.status) || device.status || '',
    device.interpretation || '',
    device.agent_id ?? '',
  ])

  downloadCsv(`m365-${slugPart(area)}-${slugPart(status)}-${stamp()}.csv`, headers, rows)
  return items.length
}
